import type { ReactNode } from "react";
import { Spinner } from "@/components/feedback/Spinner";
import { EmptyState } from "@/components/feedback/EmptyState";

interface QueryStateProps {
  isLoading: boolean;
  error: Error | { message: string } | null;
  isEmpty?: boolean;
  /**
   * Primary empty-state copy, e.g. "No signals match this filter."
   * Matches the existing usage across every current call site —
   * unchanged in meaning and precedence from the prior QueryState API.
   */
  emptyMessage?: string;
  /**
   * Optional short heading shown above emptyMessage. Purely additive —
   * existing call sites that only pass emptyMessage are unaffected and
   * get the default heading below.
   */
  emptyTitle?: string;
  /** Optional action rendered below the empty state message (e.g. a button or link). */
  emptyAction?: ReactNode;
  /**
   * Optional shape-matched loading fallback (e.g. <SkeletonTable />).
   * Falls back to the generic centered Spinner when omitted, preserving
   * existing call sites' behavior unchanged.
   */
  loadingFallback?: ReactNode;
  children: ReactNode;
}

const DEFAULT_EMPTY_TITLE = "Nothing here yet.";

/**
 * Standard loading / error / empty / content branches for a data-fetching
 * page section. Centralising this means every page's empty state and
 * error state look and behave the same way.
 */
export function QueryState({
  isLoading,
  error,
  isEmpty = false,
  emptyMessage,
  emptyTitle,
  emptyAction,
  loadingFallback,
  children,
}: QueryStateProps) {
  if (isLoading) {
    return (
      <div className="query-state query-state-loading">
        {loadingFallback ?? <Spinner size="md" />}
      </div>
    );
  }

  if (error) {
    return (
      <div className="query-state query-state-error" role="alert">
        <p>Couldn&rsquo;t load this data: {error.message}</p>
      </div>
    );
  }

  if (isEmpty) {
    // Existing call sites pass only emptyMessage — that becomes the title
    // when no separate emptyTitle is supplied, preserving today's visual
    // prominence of that text exactly.
    return (
      <EmptyState
        title={emptyTitle ?? emptyMessage ?? DEFAULT_EMPTY_TITLE}
        message={emptyTitle ? emptyMessage : undefined}
        action={emptyAction}
      />
    );
  }

  return <>{children}</>;
}
